/**
 * In this file we augment the user and account types
 * with additional properties that are useful in a centralized location.
 */
import { AppBundle } from '@/utils/app-bundles';
import { getExpiresAt, getLimitValue, isExpired } from '@/utils/plan-features';
import { UserMasqPermission } from '@customerio/hydra-permissions/masq';
import { snakeCase } from 'lodash-es';
import type { Account, AugmentedAccount, AugmentedUser, User } from './types';
import { PlanFeature } from './types';

/**
 * Augments the account object with plan and product information.
 */
// fallow-ignore-next-line complexity -- ported from the Ember account service, complexity is inherent to the plan-feature mapping
export function augmentAccount(account: Account): AugmentedAccount {
  // Enables premium label for premium trial users. Ported from the Ember account service.
  let premiumLabelEnabled = false;
  if (account.premium_trial === true && account.premium === false) {
    premiumLabelEnabled = true;
  } else if (account.gated_features?.premium_trial != null) {
    premiumLabelEnabled = true;
  }

  // An Ordway *customer* is one Ordway has a record for, which is what an `ordway_id` means — not one
  // the backend intends to bill through Ordway, which is what `billing_vendor` means. The two disagree
  // in both directions: an account switched to the Ordway vendor before its customer is provisioned has
  // the vendor and no id, and an account migrated off Ordway can keep its id. Matches Ember
  // (`models/account.ts:198`), the billing service's own `isOrdwayCustomer` atom, and
  // `use-cancel-account.ts` — this was the only place still asking the vendor.
  const isOrdwayCustomer = account.ordway_id != null && account.ordway_id.length > 0;
  const gatedFeatures = account.gated_features ?? {};

  const planFeatures: Partial<AugmentedAccount['planFeatures']> = {};
  for (const key of Object.values(PlanFeature)) {
    const feature = gatedFeatures[snakeCase(key)];
    const limit = getLimitValue(feature);
    let expired = isExpired(feature);

    /** TODO: This should be a temporary fix for a Backend API problem.
     * The current way to infer if a Journeys trial on a CDP plan has expired
     * is to check if both `journeys` *is present* on product_activations, and
     * `messaging` *is not present* on gated_features. Ported from the Ember account service. */
    if (key === PlanFeature.Messaging) {
      expired = (account.product_activations?.includes(AppBundle.Journeys) ?? false) && feature == null;
    }

    const expiresAt = getExpiresAt(feature);

    // if a feature is present on the gatedFeatures object, it is enabled (unless expired).
    let enabled = !expired && feature != null;

    // Support tier implications, ported from the Ember account service:
    // Chat implies community, email, and chat. Email implies community and email.
    // Community is only community.
    if (key === PlanFeature.SupportTierEmail && feature == null) {
      enabled = account.support_tier === 'email' || account.support_tier === 'chat';
    } else if (key === PlanFeature.SupportTierCommunity && feature == null) {
      enabled =
        account.support_tier === 'community' || account.support_tier === 'email' || account.support_tier === 'chat';
    } else if (key === PlanFeature.SupportTierChat && feature == null) {
      enabled = account.support_tier === 'chat';
    }

    // Backwards compat for stripe premium trials, ported from the Ember account service.
    if (key === PlanFeature.PremiumTrial && feature == null && !isOrdwayCustomer) {
      enabled = account.premium_trial && account.premium === false;
    }

    planFeatures[key] = {
      expired,
      enabled,
      showPremiumLabel: !enabled || premiumLabelEnabled,
      limit: limit ?? false,
      expiresAt,
    };
  }

  const activeProducts = Object.values(AppBundle).reduce(
    (activeProductsMap, app) => {
      activeProductsMap[app] = account?.product_activations?.includes(app) ?? false;
      return activeProductsMap;
    },
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    {} as Record<AppBundle, boolean>,
  );

  const now = Math.floor(Date.now() / 1000); // Current timestamp in seconds
  const supportTeamAccessEnabled = (account.access_granted_until ?? 0) > now;
  const successTeamAccessEnabled =
    (account.cs_access_granted_until ?? 0) === -1 || (account.cs_access_granted_until ?? 0) > now;

  return {
    ...account,
    activeProducts,
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    planFeatures: planFeatures as AugmentedAccount['planFeatures'],
    support_team_access_enabled: supportTeamAccessEnabled,
    success_team_access_enabled: successTeamAccessEnabled,
  };
}

/**
 * Augments the user object with masq permissions.
 */
export function augmentUser(user: User): AugmentedUser {
  const masqPermissions = user?.masq_permissions ?? UserMasqPermission.NoMasqAccess;

  const isSuperUser = masqPermissions === UserMasqPermission.SuperUser;
  const canReadWriteMasq = masqPermissions === UserMasqPermission.ReadWrite;
  const canReadOnlyMasq =
    masqPermissions === UserMasqPermission.ReadOnly || masqPermissions === UserMasqPermission.Redacted;
  const hasReadMasqAccess = isSuperUser || canReadWriteMasq || canReadOnlyMasq;

  return {
    ...user,
    name: `${user.first_name} ${user.last_name}`.trim(),
    isSuperUser,
    canReadOnlyMasq,
    canReadWriteMasq,
    noMasqAccess: masqPermissions === UserMasqPermission.NoMasqAccess || masqPermissions == null,
    masqLevelNotSet: masqPermissions === UserMasqPermission.MasqLevelNotSet,
    hasReadMasqAccess,
  };
}
